import React, { useRef } from "react";
import {
  WrenchScrewdriverIcon,
  CubeTransparentIcon,
  BuildingOffice2Icon,
  Squares2X2Icon,
  BeakerIcon,
  BoltIcon,
  LightBulbIcon,
  HomeModernIcon,
  WindowIcon,
  WrenchIcon,
  TruckIcon,
  SparklesIcon,
  ClipboardDocumentCheckIcon,
  ArrowDownTrayIcon as DownloadIcon,
} from "@heroicons/react/24/solid";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const SECTION_ICONS = {
  "Borewell & Motor": <WrenchScrewdriverIcon className="h-6 w-6 text-indigo-500" />,
  "Cement & Concrete": <CubeTransparentIcon className="h-6 w-6 text-yellow-500" />,
  "Bricks & Walls": <BuildingOffice2Icon className="h-6 w-6 text-rose-500" />,
  "Sand": <Squares2X2Icon className="h-6 w-6 text-amber-500" />,
  "Steel & Reinforcement": <BeakerIcon className="h-6 w-6 text-gray-700" />,
  "Electrical": <BoltIcon className="h-6 w-6 text-yellow-400" />,
  "Ceiling & Interiors": <LightBulbIcon className="h-6 w-6 text-pink-400" />,
  "Flooring & Bathroom Tiles": <HomeModernIcon className="h-6 w-6 text-blue-400" />,
  "Doors & Windows": <WindowIcon className="h-6 w-6 text-emerald-500" />,
  "Plumbing": <WrenchIcon className="h-6 w-6 text-sky-500" />,
  "Parking & Glasswork": <TruckIcon className="h-6 w-6 text-gray-400" />,
  "Extras": <SparklesIcon className="h-6 w-6 text-indigo-400" />,
};

function highlightBrands(value) {
  const brands = [
    "UltraTech", "Bharathi Ultra Fast", "Birla Shakti", "Priya", "Parasakthi",
    "Steel Grip", "Sree", "Finolex", "Gold Medal", "Anchor", "CRI", "CERA",
    "Ashirwadh", "African Teak", "Tandoor Banda", "Cheetah Stone", "Rakula Shed"
  ];
  let result = value;
  brands.forEach((brand) => {
    result = result.replace(
      new RegExp(`(${brand})`, "g"),
      `<span class='bg-slate-100 text-sm text-gray-700 px-2 py-0.5 rounded-full font-semibold'>$1</span>`
    );
  });
  return result;
}

const MATERIALS_SPEC = [
  {
    section: 'Borewell & Motor',
    items: [
      { label: 'Bore Depth', value: '500–600 ft' },
      { label: 'Motor Brand', value: 'CRI' },
    ],
  },
  {
    section: 'Cement & Concrete',
    items: [
      { label: 'Slab/Concrete Cement', value: 'UltraTech / Bharathi Ultra Fast' },
      { label: 'Wall Cement', value: '43 Grade (Birla Shakti / Priya / Parasakthi)' },
      { label: 'Plastering', value: 'River Sand only (no cement)' },
    ],
  },
  {
    section: 'Bricks & Walls',
    items: [
      { label: 'Red Bricks', value: 'Used for all walls' },
      { label: 'Compound Walls', value: 'Cement Bricks' },
    ],
  },
  {
    section: 'Sand',
    items: [
      { label: 'For Concrete', value: 'Robo Sand / River Sand' },
      { label: 'For Plastering', value: 'Only River Sand' },
    ],
  },
  {
    section: 'Steel & Reinforcement',
    items: [
      { label: 'Footings', value: '4 Cement Bags (6 ft depth)' },
      { label: 'Pillars', value: '4 rods (16mm) + 4 rods (12mm)' },
      { label: 'Slabs', value: '8/8 or 8/10 rods' },
      { label: 'Steel Brand', value: 'Steel Grip / Sree' },
    ],
  },
  {
    section: 'Electrical',
    items: [
      { label: 'Cables', value: 'Finolex Wires / Equivalent' },
      { label: 'Switches', value: 'Gold Medal / Anchor' },
    ],
  },
  {
    section: 'Ceiling & Interiors',
    items: [
      { label: 'False Ceiling', value: 'Gypsum – applied to 2 Bedrooms, Hall, Dining & Living Room' },
    ],
  },
  {
    section: 'Flooring & Bathroom Tiles',
    items: [
      { label: 'General Flooring', value: '₹60/sq.ft (Granite/Tiles/Marble) – Includes labor & transport' },
      { label: 'Bathroom Tiles', value: '₹200/box – 2 bathrooms per floor (1 Western, 1 Indian)' },
    ],
  },
  {
    section: 'Doors & Windows',
    items: [
      { label: 'Main Door', value: 'African Teak (₹1250 worth)' },
      { label: 'Bedroom Doors', value: '₹500 each' },
      { label: 'Bathroom Doors', value: '₹300 each' },
      { label: 'Internal Doors', value: 'Flush Doors (₹130/ft), 6 doors per floor' },
      { label: 'Windows', value: '6 Total – 4 (4x4), 1 (2x3), 1 (2x2)' },
      { label: 'Hardware', value: 'Stainless Steel' },
    ],
  },
  {
    section: 'Plumbing',
    items: [
      { label: 'Pipes', value: 'Ashirwadh' },
      { label: 'Taps', value: 'CERA (Basic model)' },
      { label: 'Toilets', value: 'Western-style (₹5000)' },
      { label: 'Water Tank', value: '1000 Litres' },
    ],
  },
  {
    section: 'Parking & Glasswork',
    items: [
      { label: 'Parking Finish', value: 'Tandoor Banda / Cheetah Stone' },
      { label: 'Glass Type', value: 'Plain Glass (general), Toughened Glass for main door design (optional)' },
    ],
  },
  {
    section: 'Extras',
    items: [
      { label: 'Staircase Cover', value: 'Optional Rakula Shed (₹50,000 extra)' },
      { label: 'Granite Window Frames', value: 'Optional extra' },
    ],
  },
];

export default function MaterialCatalogPage() {
  const sectionRef = useRef();

  const handleDownloadPDF = async () => {
    const input = sectionRef.current;
    if (!input) return;
    // Use html2canvas to capture the section
    const canvas = await html2canvas(input, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    // Calculate width/height to fit A4
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    // Keep aspect ratio
    const imgWidth = pageWidth - 40;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    pdf.addImage(imgData, "PNG", 20, 20, imgWidth, imgHeight, undefined, 'FAST');
    pdf.save("materials-used.pdf");
  };

  return (
    <div className="p-6 lg:p-10 bg-gray-50 min-h-screen font-body">
      <div className="max-w-7xl mx-auto">
        {/* Header Row */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h2 className="text-2xl lg:text-3xl font-display-bold text-gray-900">Materials Used in Your Home</h2>
            <span className="block text-xs text-gray-400 mt-1 ml-0 md:ml-1">Last Updated: Aug 2025</span>
          </div>
          <button
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow hover:bg-indigo-700 transition-colors text-sm font-semibold w-fit"
            onClick={handleDownloadPDF}
          >
            <DownloadIcon className="h-5 w-5" />
            Download PDF
          </button>
        </div>
        {/* Cards Grid */}
        <div ref={sectionRef} id="materials-used-section">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MATERIALS_SPEC.map(section => (
              <div
                key={section.section}
                className="bg-white shadow-md rounded-xl p-6 flex flex-col min-h-[320px] relative"
              >
                {/* Icon and Section Title */}
                <div className="flex items-center mb-4">
                  <div className="mr-2">{SECTION_ICONS[section.section]}</div>
                  <div className="font-bold text-lg text-gray-800">{section.section}</div>
                </div>
                {/* Items */}
                <div className="flex-1 flex flex-col">
                  {section.items.map((item, idx) => (
                    <div key={idx} className="flex flex-col py-2">
                      <span className="text-sm font-bold text-gray-600">{item.label}</span>
                      <span
                        className="text-base font-normal text-gray-700 mt-0.5"
                        dangerouslySetInnerHTML={{ __html: highlightBrands(item.value) }}
                      />
                      {/* Divider for cards with 3+ entries, except after last row */}
                      {section.items.length >= 3 && idx < section.items.length - 1 && (
                        <hr className="my-2 border-gray-100" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
} 